import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import { errorMessage, successMessage } from "../../../utils";
import {
  PHONE_NUMBER_VALIDATION,
  getPhoneNumberRule,
} from "../../../shared/constants/validation";
import { getCustomerDetailsById } from "../../Customers/api";

const defaultFormData = {
  business_name: "",
  business_branch: "",
  full_business_address: "",
  business_state: "",
  business_postcode: "",
  abn_number: "",
  business_country_code: "",
  business_phone: "",
  save_business_details: false,
};

// Fields filled from the customer's saved business details, they share the form's names
const customerBusinessFields = [
  "business_name",
  "business_branch",
  "full_business_address",
  "business_state",
  "business_postcode",
  "abn_number",
  "business_country_code",
  "business_phone",
];

// Shared by "Create FTI" and "Cancel & Re-Issue": `submitApi` sends the business details
// and `onSuccess` receives the newly issued FTI. With a `customerId` the form is prefilled
// from that customer's saved business details.
export const useFtiForm = ({
  initialValues = {},
  customerId = null,
  submitApi,
  onSuccess,
}) => {
  const [, setForceUpdate] = useState(0);
  const [state, setState] = useImmer({
    formData: { ...defaultFormData, ...initialValues },
    isSubmitting: false,
  });

  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  // Fields required by the API, re-validated on every render
  const validations = {
    business_name: formValidator.current.message(
      "Customer Name",
      state.formData.business_name,
      "required",
    ),
    full_business_address: formValidator.current.message(
      "Customer Address",
      state.formData.full_business_address,
      "required",
    ),
    business_state: formValidator.current.message(
      "State",
      state.formData.business_state,
      "required",
    ),
    business_postcode: formValidator.current.message(
      "Post Code",
      state.formData.business_postcode,
      "required",
    ),
    abn_number: formValidator.current.message(
      "Tax Payer Identification Number",
      state.formData.abn_number,
      "required",
    ),
    business_phone: formValidator.current.message(
      "Telephone Number",
      state.formData.business_phone,
      getPhoneNumberRule(state.formData.business_country_code),
      PHONE_NUMBER_VALIDATION,
    ),
  };

  // Only fills fields that are still empty, so nothing typed while it loads is overwritten
  const prefillFromCustomer = async () => {
    try {
      const { success, data } = await getCustomerDetailsById(customerId);
      if (!success || !data) return;
      setState((draft) => {
        customerBusinessFields.forEach((field) => {
          if (!draft.formData[field] && data[field]) {
            draft.formData[field] = data[field];
          }
        });
      });
    } catch (error) {
      // The form still works without the prefill, so a failed lookup is not shown
    }
  };

  const submitFti = async () => {
    setState((draft) => {
      draft.isSubmitting = true;
    });
    try {
      const { save_business_details, ...formData } = state.formData;
      const response = await submitApi({
        ...formData,
        save_business_details: save_business_details ? 1 : 0,
      });
      const { success, message, data } = response;
      if (success) {
        successMessage(message);
        // Awaited so GENERATE keeps loading while the new FTI's PDF is exported,
        // the details page then asks whether to download or print it
        await onSuccess(data);
      } else {
        errorMessage(message);
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ?? "Failed to issue full tax invoice",
      );
    } finally {
      setState((draft) => {
        draft.isSubmitting = false;
      });
    }
  };

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setState((draft) => {
      draft.formData[name] = type === "checkbox" ? checked : value;
    });
  };

  const onSubmit = () => {
    if (formValidator.current.allValid()) {
      submitFti();
    } else {
      formValidator.current.showMessages();
      setForceUpdate((previous) => previous + 1);
    }
  };

  useEffect(() => {
    if (customerId) prefillFromCustomer();
  }, [customerId]);

  return { state, validations, onChange, onSubmit };
};
